import { useCallback, useMemo, useState } from 'react';
import throttle from 'transmute/throttle';
import { useScrollWindowRef } from '../table/context/ScrollWindowRefContext';

//the number of pixels inset from the left and right border that will trigger scrolling in that direction when the mouse is within it
const SCROLL_MARGIN = 200;
//a multiplier that can change how fast scrolling happens
const SCROLL_SPEED = 1.5;
export const LEFT_POSITION = 'left';
export const RIGHT_POSITION = 'right';
//we throttle this because trackpad/touch events fire really fast often times 4-5 times within a single tick, whereas mouse movements fire much less
const throttledScrollLeftBy = throttle(200, (container, left) => {
  container.scrollBy({
    left,
    behavior: 'smooth'
  });
});
const scrollListener = (e, container) => {
  if (!container) {
    return;
  }
  try {
    const mouseXPos = e.clientX;
    const containerBoundingClientRect = container.getBoundingClientRect();
    const containerLeft = containerBoundingClientRect.left;
    const containerRight = containerBoundingClientRect.right;
    const leftDelta = Math.abs(mouseXPos - containerLeft);
    const rightDelta = Math.abs(mouseXPos - containerRight);

    //if we're close to the left edge, scroll left
    //the scroll amount is linear to how "close" to the edge we are
    if (leftDelta <= SCROLL_MARGIN) {
      const scrollLeftBy = (leftDelta - SCROLL_MARGIN) * SCROLL_SPEED;
      throttledScrollLeftBy(container, scrollLeftBy);
      return;
    }
    //if we're close to the right edge, scroll right
    //the scroll amount is linear to how "close" to the edge we are
    if (rightDelta <= SCROLL_MARGIN) {
      const scrollRightBy = (SCROLL_MARGIN - rightDelta) * SCROLL_SPEED;
      throttledScrollLeftBy(container, scrollRightBy);
      return;
    }
  } catch (err) {
    //errors are to be expected if a scrollListener is throttled and then a re-render (such as crm-object-table polling, or the parent re-rendering for any reason)
    //which will cause the container to no longer be in the dom, and getBoundingClientRect to throw
    //this will not cause problems for the user
  }
};
export const encodeColumnHeaderDNDData = ({
  columnName,
  columnNamespace
}) => {
  return `FDT_COLUMN_HEADER::${columnNamespace}::${columnName}`;
};
export const decodeColumnHeaderDNDData = ({
  dndData
}) => {
  const items = dndData.split('::');
  if (items.length < 3 || items[0] !== 'FDT_COLUMN_HEADER') {
    return null;
  }
  return {
    columnNamespace: items[1],
    columnName: items[2]
  };
};

/**
 * Leverages native html5 dnd api to allow dragging and dropping column headers.
 *
 * Make sure to pass through these props to the wrapper div element:
 * - onDragEnter
 * - onDragLeave
 * - onDrop
 * - onDragOver
 * - onDragStart
 * - onDrag
 */
export const useColumnDragAndDrop = ({
  reorderable,
  columnIndex,
  columnName,
  columnNamespace,
  onColumnOrderChange,
  hasNewTableHeader
}) => {
  const [isActiveDropTarget, setIsActiveDropTarget] = useState(false);
  const {
    element: scrollWindowRef
  } = useScrollWindowRef();
  const [currentDropPosition, setCurrentDropPosition] = useState(LEFT_POSITION);
  const onDragStart = useCallback(e => {
    if (!reorderable) {
      return;
    }
    e.dataTransfer.setData('text', encodeColumnHeaderDNDData({
      columnNamespace,
      columnName
    }));
  }, [columnName, columnNamespace, reorderable]);
  const onDrag = useCallback(e_0 => {
    if (!reorderable) {
      return;
    }
    scrollListener(e_0, scrollWindowRef);
  }, [reorderable, scrollWindowRef]);
  const onDragEnter = useCallback(e_1 => {
    if (!reorderable) {
      return;
    }
    if (hasNewTableHeader) {
      const headerRect = e_1.currentTarget.getBoundingClientRect();
      const midpoint = headerRect.width / 2 + headerRect.left;
      if (e_1.clientX < midpoint) {
        setCurrentDropPosition(LEFT_POSITION);
      } else {
        setCurrentDropPosition(RIGHT_POSITION);
      }
    }
    e_1.preventDefault();
    setIsActiveDropTarget(true);
  }, [reorderable, hasNewTableHeader]);
  const onDragLeave = useCallback(e_2 => {
    if (!reorderable) {
      return;
    }
    e_2.preventDefault();
    setIsActiveDropTarget(false);
  }, [reorderable]);
  const onDragOver = useCallback(e_3 => {
    if (!reorderable) {
      return;
    }

    // This exists to fix a bug where drag-and-drop was broken on the product history card on the contact records page
    // See https://git.hubteam.com/HubSpot/ProductSupport/issues/111557
    e_3.stopPropagation();
    e_3.preventDefault();
    setIsActiveDropTarget(true);
  }, [reorderable]);

  //when the user lets go of the mouse and the drag is complete
  const onDrop = useCallback(e_4 => {
    if (!reorderable) {
      return;
    }
    e_4.preventDefault();
    try {
      const dndData = e_4.dataTransfer.getData('text');
      const parsedData = decodeColumnHeaderDNDData({
        dndData
      });
      if (!parsedData) {
        return;
      }
      const nextIndex = currentDropPosition === LEFT_POSITION ? columnIndex : columnIndex + 1;
      onColumnOrderChange({
        columnNamespace: parsedData.columnNamespace,
        columnName: parsedData.columnName,
        nextIndex: hasNewTableHeader ? nextIndex : columnIndex
      });
    } catch (err) {
      //this is to be expected when anyone drops a file or image onto the page
      return;
    }
  }, [columnIndex, currentDropPosition, onColumnOrderChange, reorderable, hasNewTableHeader]);
  return useMemo(() => ({
    onDragEnter,
    onDragLeave,
    isActiveDropTarget,
    dropPosition: currentDropPosition,
    onDrop,
    onDragOver,
    onDragStart,
    onDrag
  }), [isActiveDropTarget, currentDropPosition, onDragEnter, onDragLeave, onDrop, onDragOver, onDragStart, onDrag]);
};